import { type FC, useRef } from 'react'

import { Section } from '@factorialco/deprecated-design-system'
import {
  JobCatalogRoleBasicInfoFragment,
  JobCatalogUpdateRoleBasicInfoDocument,
} from 'generated/resources/graphql'

import TextEditor, { EditorHandle } from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

// TODO: avoid collections when refactor ⤵
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'

interface Props {
  role: JobCatalogRoleBasicInfoFragment
}

interface FormValues {
  name: string
  description?: string | null
}

export const RoleBasicInfoForm: FC<Props> = ({ role }) => {
  const textEditorRef = useRef<EditorHandle>(null)
  const [updateBasicInfo] = useMutation(JobCatalogUpdateRoleBasicInfoDocument, {
    extractorFn: (data) => data.jobCatalogMutations.updateJobcatalogRole,
  })

  const form = useForm<FormValues>({
    defaultValues: {
      name: role.name,
      description: role.description,
    },
    validate(values) {
      const errors: FormErrors<FormValues> = {}

      if (!values.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async ({ name, description }, { resetForm }) => {
      const result = await updateBasicInfo({
        variables: {
          id: role.id,
          name,
          description,
        },
      })

      const handled = handleResult(result, (errors) => {
        form.setErrors({ ...errors })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        resetForm({ values: { name, description } })
        track(trackingEvents.jobCatalog.addJobRoleDescriptionSubmit)
        // TODO: This is a hack to force the role to be fetched again should replaced with graphql in the job roles page
        await jobRolesCollection.fetch()
      }

      return handled
    },
  })

  return (
    <Section.Form
      illustration="settings"
      title={i18n.t('common.basic_info')}
      description={i18n.t('job_catalog.basic_info_description')}
      isDirty={form.dirty}
      submitLabel={i18n.t('common.submit')}
      disabled={!role}
      onSubmit={form.handleSubmit}
    >
      <TextField
        mandatory
        label={i18n.t('job_catalog.job_name')}
        placeholder={i18n.t('job_catalog.job_name')}
        value={form.values.name}
        onChange={(name) => form.setFieldValue('name', name)}
        error={form.errors.name}
      />
      <TextEditor
        ref={textEditorRef}
        onChange={(description) => form.setFieldValue('description', description)}
        placeholder=""
        initialEditorState={{ data: form.values.description }}
        height="s140"
      />
    </Section.Form>
  )
}
